
import {
    AddDataSharedFunc, DashboardSharedFilterFunc, DashboardSharedFilterKey,
    IDashboardSharedChartData, IDashboardSharedDimensionsViewModel,
} from "models/common";
import { IFilters, IFilterKeys, DimensionsById, ISourceData, IDimension, IDimMDT } from "models/core";

//Plots for this dashboard
import * as ReviewsByService from "./reviewsByService";
import * as PatientsByService from "./patientsByService";
import * as DiagnosesByService from "./diagnosesByService";
import * as ReviewsByPeriod from "./reviewsByPeriodHierarchy";
import * as PatientsByPeriod from "./patientsByPeriodHierarchy";
import * as DiagnosesByPeriod from "./diagnosesByPeriodHierarchy";
import * as DataQualityByPeriod from "./dataQualityByPeriod";
import * as PrimarySite from "./primarySiteHierarchy";
import * as HhsResidenceGroup from "./hhsResidenceGroup";
import * as HhsResidenceGroupByPeriod from "./hhsResidenceGroupByPeriod";
import * as Morphology from "./morphology";
import * as Remoteness from "../../common/plotdata/remoteness";
import * as HHSOfResidence from "../../common/plotdata/hhsOfResidence";
import * as FirstTreatment from "./firstTreatment";
import * as TimeToFirstTreatment from "./timeToFirstTreatment";
import * as DataQualityByIndicator from "./dataQualityByIndicator";
import { IDashboardViewModel } from "./viewModel";

export { type IDashboardDataQualityViewModel, type IDashboardViewModel } from "./viewModel";

export interface IDashboardSource {
    eligible: IDashboardViewModel[];
    ineligible: IDashboardViewModel[];
	noProfiles: Partial<IDashboardViewModel>[];
}

export interface IDashboardDimensionsViewModel extends IDashboardSharedDimensionsViewModel {
    hhsResidenceGroups?: DimensionsById;
    dataQualityIndicators?: DimensionsById;
    monthsArray?: Array<IDimension>;
    yearsArray?: Array<IDimension>;
    months?: { [monthId: number]: string };
    years?: { [yearId: number]: string };
    dqCompFacility?: { [facilityYearKey: string]: number };
    dqCompState?: { [facilityYearKey: string]: number };
    oneYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    threeYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    fiveYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    tenYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    kmSurvivalMDT?: {[mdtCancerKey: string]: { survivalTime: string, mdtKMSurvival: number, stateKMSurvival: number }};
}

export interface IChartData extends IDashboardSharedChartData {
    reviewsByService: ReviewsByService.IPlotData;
    patientsByService: PatientsByService.IPlotData;
    diagnosesByService: DiagnosesByService.IPlotData;
    reviewsByPeriod: ReviewsByPeriod.IHierarchyPlotData;
    patientsByPeriod: PatientsByPeriod.IHierarchyPlotData;
    diagnosesByPeriod: DiagnosesByPeriod.IHierarchyPlotData;
    primarySite: PrimarySite.IHierarchyPlotData;
    morphology: Morphology.IPlotData;
    remoteness: Remoteness.IPlotData;
    hhsOfResidence: HHSOfResidence.IPlotData;
    firstTreatment: FirstTreatment.IPlotData;
    timeToFirstTreatment: TimeToFirstTreatment.IPlotData;
    hhsResidenceGroup: HhsResidenceGroup.IPlotData;
    hhsResidenceGroupByPeriod: HhsResidenceGroupByPeriod.IHierarchyPlotData;
    dataQualityByPeriod: DataQualityByPeriod.IHierarchyPlotData;
    dataQualityByIndicator: DataQualityByIndicator.IPlotData;
    mdts: IDimMDT[];
    month: IDimension[];
    year: IDimension[];
    dqCompFacility:  { [facilityYearKey: string]: number };
    dqCompState: { [facilityYearKey: string]: number };
    oneYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    threeYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    fiveYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    tenYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    kmSurvivalMDT?: {[mdtCancerKey: string]: { survivalTime: string, mdtKMSurvival: number, stateKMSurvival: number }};
    //Computations
    totalReviews: number;
    totalPresentations: number;
    totalManyPresentations: number;
    totalPatients: number;
    totalDiagnoses: number;
    totalDataQualityEligibleDiagnoses: number;
    totalMaleDiagnoses: number;
    totalIndigenousDiagnoses: number;
    totalMDTs: number;
    totalDisadvantaged: number;
    totalComorbidity2Plus: number;
    totalLateStage: number;
	totalNoProfiles: number;
    medianAgeAtDiagnosis: number;
    averageDataQuality: number;
    // Export
    filteredReferralIdCsv: string;
    excludedReferralIdCsv: string;

}

const FilterKey: IFilterKeys = {
    HHSResidenceGroup: { key: "HHSResidenceGroup", label: "HHS residence group" },
    HHSOfResidence: { key: "HHSOfResidence", label: "HHS of residence"},
    DataQualityEligible: { key: "DataQualityEligible", label: "Data quality eligible" },
    PrimarySite: { key: "PrimarySite", label: "Primary site" },
    Morphology: { key: "Morphology", label: "Morphology" },
    Remoteness: { key: "Remoteness", label: "Rurality" },
    FirstTreatment: { key: "FirstTreatment", label: "First Treatment" },
    TimeToFirstTreatment: { key: "TimeToFirstTreatment", label: "Time To First Treatment" },
    DiagMonth: { key: "DiagMonth", label: "Diagnosis Month" },
    DiagYear: { key: "DiagYear", label: "Diagnosis Year" },
}

export const DashboardFilterKeys: IFilterKeys = {
    ...DashboardSharedFilterKey,
    ...FilterKey
}

export type Filters = IFilters<typeof DashboardFilterKeys>;
export type DashboardFilterFunc = DashboardSharedFilterFunc<IDashboardViewModel>;
export type AddDataFunc = AddDataSharedFunc<IDashboardViewModel, ISourceData>;

